import React, { Component } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
;
import { Button, Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';;;
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  setBreadcrumbs,
  reduceServerErrors,
  ConfirmDeletionModal,
  UiToastAdapter,
  GuidelinesCard,
} from 'src/ui';
import { getToken } from 'src/auth';
import { getUser, fetchLoggedInUser } from 'src/user';
import {
  getAccount,
  fetchAccount,
  deleteAccountProduct,
  addAccountProduct,
  updateAccountProduct,
  addAccountProductCOA,
  deleteAccountProductCOA,
} from 'src/account';
import dayjs from 'dayjs-ext';
import _ from 'lodash';
import { ProductsTable, AddProductModal } from '../components';

class ProductsListPage extends Component {
  state = {
    loading: true,
    deletingProduct: null,
    editingProduct: null,
    isAddingProduct: false,
  };

  guidelines = [
    `In this section we are going to add all retail, wholesale, and eCommerce products sold by your company. This list will include product name, batch number, and all COAs associated with an individual product.`,
    `Be sure to include all COAs that pertain to a product as this is important from a compliance perspective.`,
    `If you need help adding a large list of products and COAs feel free to contact support for assistance.`,
  ];

  async componentDidMount() {
    const {
      setBreadcrumbs,
      fetchLoggedInUser,
      fetchAccount,
      token,
    } = this.props;

    setBreadcrumbs([{ label: 'Products', href: '/products' }]);

    if (token.isValid()) {
      const { user } = this.props;
      if (!user.isValid()) {
        await fetchLoggedInUser();
      }

      if (user.accountId) {
        await fetchAccount(user.accountId);

        const { account } = this.props;
        if (account.isError()) {
          reduceServerErrors(account);
        }
      }
    }

    this.setState({ loading: false });
  }

  onDeleteProduct = product => {
    this.setState({ deletingProduct: product });
  };

  onCancelDeleteProduct = () => {
    this.setState({ deletingProduct: null });
  };

  onConfirmDeleteProduct = async () => {
    const { user, deleteAccountProduct } = this.props;
    const { deletingProduct } = this.state;

    this.setState({ loading: true });
    this.onCancelDeleteProduct();

    await deleteAccountProduct({
      accountId: user.accountId,
      productId: deletingProduct.id,
    });

    this.setState({ loading: false });

    const { account } = this.props;
    if (account.isError()) {
      return reduceServerErrors(account);
    }
    toast(UiToastAdapter('Product deleted successfully!', 'success'));
    return undefined;
  };

  onAddProduct = () => {
    this.setState({ isAddingProduct: true });
  };

  onCancelAddProduct = form => {
    this.setState({
      isAddingProduct: false,
      editingProduct: null,
    });
    if (form) {
      form.reset();
    }
  };

  onEditProduct = product => {
    this.setState({
      editingProduct: {
        ...product,
        coas: [...product.coas],
        expirationDate:
          product.expirationDate &&
          dayjs(product.expirationDate)
            .utc()
            .toDate(),
      },
    });
  };

  onSubmitProduct = async values => {
    const { user, addAccountProduct, updateAccountProduct } = this.props;
    const { editingProduct } = this.state;
    const { coas } = values;

    const data = {
      accountId: user.accountId,
      ...values,
    };
    if (editingProduct) {
      data.productId = editingProduct.id;
    }
    const { payload } = await (editingProduct
      ? updateAccountProduct(data)
      : addAccountProduct(data));

    const { account } = this.props;

    if (account.isError()) {
      return reduceServerErrors(account);
    }
    return this.uploadCoas(payload.id, coas);
  };

  // eslint-disable-next-line consistent-return
  async uploadCoas(id, coas) {
    if (coas instanceof Array) {
      const {
        user,
        addAccountProductCOA,
        deleteAccountProductCOA,
      } = this.props;
      const { editingProduct } = this.state;

      const addedCoas = coas
        .filter(file => !file.url)
        .map(file =>
          addAccountProductCOA({
            accountId: user.accountId,
            productId: id,
            file,
          })
        );
      const deletedCoas = (editingProduct
        ? _.difference(editingProduct.coas, coas)
        : []
      ).map(file =>
        deleteAccountProductCOA({
          accountId: user.accountId,
          productId: id,
          coaId: file.id,
        })
      );
      const promises = [...addedCoas, ...deletedCoas];

      await Promise.all(promises);

      const { account } = this.props;
      if (account.isError()) {
        return reduceServerErrors(account);
      }
    }

    this.onCancelAddProduct();
    toast(UiToastAdapter('Product added successfully!', 'success'));
  }

  render() {
    const { account, user } = this.props;
    const {
      loading,
      deletingProduct,
      isAddingProduct,
      editingProduct,
    } = this.state;

    return (
      <>
        <Columns isMultiline>
          <Column isSize="1/2">
            <MuiTitle isSize="4" hasTextColor="info" tag="h2">
              Product COAs
            </MuiTitle>
          </Column>
          <Column isSize="1/2">
            <Button
              isColor="gradient-bright"
              isPulled="right"
              onClick={this.onAddProduct}
            >
              <span className="icon is-left">
                <FontAwesomeIcon icon={['fal', 'plus']} />
              </span>
              <span>Add Product COA</span>
            </Button>
          </Column>
          <Column>
            <ProductsTable
              loading={loading}
              products={account.products || []}
              onDeleteProduct={this.onDeleteProduct}
              onEditProduct={this.onEditProduct}
              emptyOnClickButton={this.onAddProduct}
            />
          </Column>
          {!user.isBankAdmin && (
            <Column isSize="narrow">
              <GuidelinesCard guidelines={this.guidelines} />
            </Column>
          )}
        </Columns>
        <AddProductModal
          isActive={isAddingProduct || !!editingProduct}
          onCancel={this.onCancelAddProduct}
          onSubmit={this.onSubmitProduct}
          initialValues={editingProduct || {}}
        />
        <ConfirmDeletionModal
          isActive={!!deletingProduct}
          onClickCancel={this.onCancelDeleteProduct}
          onClickConfirm={this.onConfirmDeleteProduct}
          title={(deletingProduct || {}).name || ''}
        />
      </>
    );
  }
}

ProductsListPage.propTypes = {
  fetchLoggedInUser: propTypes.func.isRequired,
  fetchAccount: propTypes.func.isRequired,
  addAccountProduct: propTypes.func.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  deleteAccountProduct: propTypes.func.isRequired,
  addAccountProductCOA: propTypes.func.isRequired,
  deleteAccountProductCOA: propTypes.func.isRequired,
  updateAccountProduct: propTypes.func.isRequired,
  token: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  account: propTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    token: getToken(state),
    user: getUser(state),
    account: getAccount(state),
  };
}

const connectedLocatioinsListPage = connect(
  mapStateToProps,
  {
    fetchLoggedInUser,
    fetchAccount,
    setBreadcrumbs,
    addAccountProduct,
    updateAccountProduct,
    deleteAccountProduct,
    addAccountProductCOA,
    deleteAccountProductCOA,
  }
)(ProductsListPage);

export { connectedLocatioinsListPage as ProductsListPage };
